import React, { useState, useEffect } from "react";
import {
    Plus,
    Edit2,
    Trash2,
    Users,
    FileText,
    LogOut,
    Image,
    Video,
    AlertTriangle,
} from "lucide-react";
import Modal from "./Modal";
import Alert from "./Alert";


const Dashboard = () => {
    const [activeTab, setActiveTab] = useState('content');
    const [items, setItems] = useState([]);
    const [subscribers, setSubscribers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [currentItem, setCurrentItem] = useState(null);
    const apiUrl = process.env.REACT_APP_API_URL;

    const [formData, setFormData] = useState({
        title: "",
        description: "",
        type: "image", // default type
        image_url: "",
        image_file: null,
        video_url: "",
        video_file: null,
        slider_images: []
    });

    useEffect(() => {
        if (activeTab === 'content') {
            fetchItems();
        } else {
            fetchSubscribers();
        }
    }, [activeTab]);

    const fetchSubscribers = async () => {
        try {
            setIsLoading(true);
            const response = await fetch(`${apiUrl}api/subscribers`);
            const data = await response.json();
            setSubscribers(data);
        } catch (error) {
            console.error("Error fetching subscribers:", error);
        } finally {
            setIsLoading(false);
        }
    };
    const handleDeleteSubscriber = async (subscriberId) => {
        try {
            const response = await fetch(
                `${apiUrl}api/subscribers/${subscriberId}`,
                {
                    method: "DELETE",
                }
            );

            if (!response.ok) throw new Error("Failed to delete subscriber");

            await fetchSubscribers();
            setIsAlertOpen(false);
            setDeleteItemId(null);
        } catch (error) {
            console.error("Error deleting subscriber:", error);
        }
    };
    const fetchItems = async () => {
        try {
            setIsLoading(true);
            const response = await fetch(`${apiUrl}api/content`);
            const data = await response.json();
            setItems(data);
        } catch (error) {
            console.error("Error fetching items:", error);
        } finally {
            setIsLoading(false);
        }
    };


    // In Dashboard.js
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const formDataToSend = new FormData();

            // Add basic fields
            formDataToSend.append('title', formData.title);
            formDataToSend.append('description', formData.description || '');

            // Handle slider images first
            // if (formData.slider_images?.length > 0) {
            //     // Convert FileList to Array and append each file
            //     Array.from(formData.slider_images).forEach(file => {
            //         if (file instanceof File) {
            //             formDataToSend.append('slider_images', file);
            //         }
            //     });
            // }

            if (currentItem) {
                const retainedSliderImages = formData.slider_images
                    .filter(image => typeof image === 'string'); // Keep only URLs, not File objects
                formDataToSend.append('retained_slider_images', JSON.stringify(retainedSliderImages));
            }

            // Handle new slider images
            if (formData.slider_images) {
                const newSliderImages = formData.slider_images
                    .filter(image => image instanceof File);
                newSliderImages.forEach(file => {
                    formDataToSend.append('slider_images', file);
                });
            }

            // Handle single image
            if (formData.image_file) {
                formDataToSend.append('image_file', formData.image_file);
            }

            // Handle video
            if (formData.video_file) {
                formDataToSend.append('video_file', formData.video_file);
            }

            // Add existing URLs if editing
            if (currentItem) {
                if (formData.image_url && !formData.image_file) {
                    formDataToSend.append('image_url', formData.image_url);
                }
                if (formData.video_url && !formData.video_file) {
                    formDataToSend.append('video_url', formData.video_url);
                }
            }

            // Log FormData contents for debugging
            for (let pair of formDataToSend.entries()) {
                console.log(pair[0] + ': ' + pair[1]);
            }

            const url = currentItem
                ? `${apiUrl}api/content/${currentItem.id}`
                : `${apiUrl}api/content`;

            const response = await fetch(url, {
                method: currentItem ? 'PUT' : 'POST',
                body: formDataToSend,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to save content');
            }

            const result = await response.json();
            console.log('Content saved successfully:', result);

            await fetchItems();
            handleCloseModal();

        } catch (error) {
            console.error('Error saving content:', error);
            alert(`Failed to save content: ${error.message}`);
        }
    };
    const handleDelete = async () => {
        try {
            const response = await fetch(
                `${apiUrl}api/content/${deleteItemId}`,
                {
                    method: "DELETE",
                }
            );

            if (!response.ok) throw new Error("Failed to delete item");

            await fetchItems();
            setIsAlertOpen(false);
            setDeleteItemId(null);
        } catch (error) {
            console.error("Error deleting item:", error);
        }
    };

    const handleEdit = (item) => {
        setCurrentItem(item);
        setFormData({
            title: item.title,
            description: item.description,
            image_url: item.image_url || "",
            video_url: item.video_url || "",
            slider_images: item.slider_images || [],
        });
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setCurrentItem(null);
        setFormData({
            title: "",
            description: "",
            image_url: "",
            video_url: "",
        });
        setIsModalOpen(false);
    };

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = "/home";
    };
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return 'N/A';
        }
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      };
      
    
    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white">
            {/* Navigation */}
            <nav className="bg-gray-800 border-b border-gray-700 shadow-md">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <h1 className="text-2xl font-bold text-yellow-400">
                          Pages
                        </h1>
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition"
                        >
                            <LogOut size={20} />
                            Logout
                        </button>
                    </div>
                </div>
            </nav>

            {/* Tabs */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex space-x-4 border-b border-gray-700">
                    <button
                        className={`flex items-center px-4 py-2 font-medium rounded-t-lg transition ${activeTab === 'content'
                                ? 'bg-gray-800 text-white border-b-2 border-yellow-400'
                                : 'text-gray-400 hover:text-white'
                            }`}
                        onClick={() => setActiveTab('content')}
                    >
                        <FileText size={20} className="mr-2" />
                        Content
                    </button>
                    <button
                        className={`flex items-center px-4 py-2 font-medium rounded-t-lg transition ${activeTab === 'subscribers'
                                ? 'bg-gray-800 text-white border-b-2 border-yellow-400'
                                : 'text-gray-400 hover:text-white'
                            }`}
                        onClick={() => setActiveTab('subscribers')}
                    >
                        <Users size={20} className="mr-2" />
                        Subscribers
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {activeTab === 'content' ? (
                    <div className="bg-gray-800 rounded-lg shadow-lg">
                        <div className="p-6 border-b border-gray-700">
                            <div className="flex justify-between items-center">
                                <h2 className="text-xl font-semibold">Content List</h2>
                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
                                >
                                    <Plus size={20} />
                                    Add New
                                </button>
                            </div>
                        </div>

                        {/* Content Items */}
                        <div className="divide-y divide-gray-700">
                            {isLoading ? (
                                <div className="p-6 text-center text-gray-400">Loading...</div>
                            ) : items.length === 0 ? (
                                <div className="p-6 text-center text-gray-400">
                                    No content items found. Click "Add New" to create one.
                                </div>
                            ) : (
                                items.map((item) => (
                                    <div
                                        key={item.id}
                                        className="p-6 hover:bg-gray-700 transition"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <h3 className="text-lg font-medium">{item.title}</h3>
                                                <p className="mt-1 text-gray-400">{item.description}</p>
                                                <div className="mt-2 flex gap-4">
                                                    {item.image_url && (
                                                        <div className="flex items-center text-sm text-gray-400">
                                                            <Image size={16} className="mr-1" />
                                                            {/* {item.image_url} */}
                                                        </div>
                                                    )}
                                                    {item.video_url && (
                                                        <div className="flex items-center text-sm text-gray-400">
                                                            <Video size={16} className="mr-1" />
                                                            {/* {item.video_url} */}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex gap-2 ml-4">
                                                <button
                                                    onClick={() => handleEdit(item)}
                                                    className="p-2 text-blue-400 hover:bg-blue-500 rounded-full transition"
                                                >
                                                    <Edit2 size={20} />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setDeleteItemId(item.id);
                                                        setIsAlertOpen(true);
                                                    }}
                                                    className="p-2 text-red-400 hover:bg-red-500 rounded-full transition"
                                                >
                                                    <Trash2 size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                ) : (
                    // Subscribers Section
                    <div className="bg-gray-800 rounded-lg shadow-lg">
                        <div className="p-6 border-b border-gray-700">
                            <div className="flex justify-between items-center">
                                <h2 className="text-xl font-semibold">Subscribers </h2>
                                <div className="text-sm text-gray-400">
                                    Total Subscribers: {subscribers.length}
                                </div>
                            </div>
                        </div>

                        <div className="divide-y divide-gray-700">
                            {isLoading ? (
                                <div className="p-6 text-center text-gray-400">Loading...</div>
                            ) : subscribers.length === 0 ? (
                                <div className="p-6 text-center text-gray-400">
                                    No subscribers found.
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-700">
                                        <thead className="bg-gray-900">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                                    Email
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                                    Date Subscribed
                                                </th>
                                                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                                    Actions
                                                </th> */}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-700">
                                            {subscribers.map((subscriber) => (
                                                <tr key={subscriber._id} className="hover:bg-gray-700">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                        {subscriber.email}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                                                        {formatDate(subscriber.created_at )}
                                                    </td>
                                                    {/* <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                        <button
                                                            onClick={() => {
                                                                setDeleteItemId(subscriber._id);
                                                                setIsAlertOpen(true);
                                                            }}
                                                            className="p-2 text-red-400 hover:bg-red-500 hover:text-white rounded-full transition"
                                                        >
                                                            <Trash2 size={20} />
                                                        </button>
                                                    </td> */}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </main>

            {/* Form Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={currentItem ? "Edit Content" : "Add New Content"}
                onSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
            />

            {/* Delete Confirmation Alert */}
            <Alert
                isOpen={isAlertOpen}
                onClose={() => {
                    setIsAlertOpen(false);
                    setDeleteItemId(null);
                }}
                onConfirm={handleDelete}
                message="Are you sure you want to delete this item? This action cannot be undone."
                icon={<AlertTriangle size={40} />}
            />
        </div>
    );
};

export default Dashboard;
